import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { heroContentAlt, portfolioProjectsAlt } from '../mockAlt';
import { Button } from '../components/ui/button';

const HomeAlt = () => {
  const featuredProjects = portfolioProjectsAlt.slice(0, 4);

  return (
    <div className="bg-white">
      {/* Hero Section - Bold Typography */}
      <section className="min-h-screen flex items-center pt-20 px-6 lg:px-12 bg-white">
        <div className="max-w-screen-2xl mx-auto w-full py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left: Massive Typography */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.h1 
                  className="text-[15vw] lg:text-[12vw] font-black uppercase leading-none tracking-tighter mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.2 }}
                >
                  <span className="block">{heroContentAlt.headline}</span>
                  <motion.span 
                    className="block"
                    initial={{ color: '#000000' }}
                    animate={{ color: '#FF4500' }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                  >
                    {heroContentAlt.subheadline}
                  </motion.span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="text-xl lg:text-2xl font-semibold max-w-lg mb-8"
                >
                  {heroContentAlt.description}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                >
                  <Link
                    to="/portfolio"
                    className="inline-flex items-center bg-black text-white px-8 py-4 font-bold text-lg hover:bg-gray-900 transition-colors group"
                  >
                    VIEW WORK
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </Link>
                </motion.div>
              </motion.div>
            </div>

            {/* Right: Stats Grid */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="grid grid-cols-2 gap-6"
              >
                {heroContentAlt.stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
                    className="bg-black text-white p-8 aspect-square flex flex-col justify-center"
                  >
                    <div className="text-5xl font-black mb-2">{stat.number}</div>
                    <div className="text-sm font-bold uppercase tracking-wider text-gray-400">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work - Grid Layout */}
      <section className="py-24 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-7xl lg:text-9xl font-black uppercase leading-none mb-4">
              SELECTED
              <br />
              WORK
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/alt/portfolio/${project.id}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden bg-black mb-4">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                      className="aspect-[4/3]"
                    >
                      <img
                        src={project.thumbnailUrl}
                        alt={project.title}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                      />
                    </motion.div>
                    {/* Color overlay on hover */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 0.2 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0"
                      style={{ backgroundColor: project.color }}
                    />
                  </div>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                        {project.category}
                      </div>
                      <h3 className="text-3xl lg:text-4xl font-black uppercase leading-tight mb-2">
                        {project.title}
                      </h3>
                      <p className="text-lg font-medium text-gray-600">{project.subtitle}</p>
                    </div>
                    <ArrowUpRight 
                      className="text-gray-400 group-hover:text-black group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" 
                      size={32} 
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 text-center"
          >
            <Link
              to="/portfolio"
              className="inline-flex items-center text-2xl font-black uppercase border-b-4 border-black hover:border-transparent transition-colors"
            >
              View All Work
              <ArrowRight className="ml-3" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Process Section - Split Layout */}
      <section className="py-24 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-6xl lg:text-7xl font-black uppercase leading-none mb-8">
                HOW WE
                <br />
                <span className="text-gray-600">WORK</span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-12"
            >
              {[
                { num: '01', title: 'STRATEGY', desc: 'Deep dive into your business goals and target audience' },
                { num: '02', title: 'PRODUCTION', desc: 'Cinematic filming with professional crew and equipment' },
                { num: '03', title: 'POST', desc: 'Expert editing, color grading, and sound design' },
                { num: '04', title: 'DELIVERY', desc: 'Optimized formats for every platform' }
              ].map((phase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex gap-6"
                >
                  <div className="text-4xl font-black text-gray-700">{phase.num}</div>
                  <div>
                    <h3 className="text-2xl font-black uppercase mb-2">{phase.title}</h3>
                    <p className="text-gray-400 text-lg">{phase.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Full Width */}
      <section className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-screen-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-7xl lg:text-9xl font-black uppercase leading-none mb-12">
              READY TO
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
                CREATE?
              </span>
            </h2>
            <Link
              to="/contact"
              className="inline-flex items-center bg-black text-white px-12 py-6 text-xl font-black uppercase hover:bg-gray-900 transition-colors group"
            >
              START PROJECT
              <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomeAlt;
